import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Check, Loader2 } from 'lucide-react';

export type CardSavePhase = 'idle' | 'saving' | 'saved';

const SAVED_VISIBLE_MS = 2200;
/** Keep the wheel on screen even when the save itself is very fast. */
const SPINNER_MIN_MS = 700;

/**
 * Corner of an autosave card. A spinner plays while that card is saving,
 * then a Saved pill, then the completion check returns.
 */
export function CardSaveBadge({
  phase,
  complete = false,
}: {
  phase: CardSavePhase;
  complete?: boolean;
}): ReactElement | null {
  const [showSaved, setShowSaved] = useState(false);
  const [holdSpinner, setHoldSpinner] = useState(false);
  const skipInitialSaved = useRef(phase === 'saved');
  const savingStarted = useRef<number | null>(null);

  useEffect(() => {
    if (skipInitialSaved.current) {
      skipInitialSaved.current = false;
      if (phase === 'saved') return undefined;
    }
    if (phase === 'saving') {
      if (savingStarted.current == null) savingStarted.current = Date.now();
      setShowSaved(false);
      setHoldSpinner(false);
      return undefined;
    }
    if (phase !== 'saved') {
      savingStarted.current = null;
      setShowSaved(false);
      setHoldSpinner(false);
      return undefined;
    }
    const started = savingStarted.current ?? Date.now();
    savingStarted.current = null;
    const wait = Math.max(0, SPINNER_MIN_MS - (Date.now() - started));
    setHoldSpinner(wait > 0);
    const showTimer = window.setTimeout(() => {
      setHoldSpinner(false);
      setShowSaved(true);
    }, wait);
    const hideTimer = window.setTimeout(() => setShowSaved(false), wait + SAVED_VISIBLE_MS);
    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, [phase]);

  const mode = phase === 'saving' || holdSpinner ? 'saving' : showSaved ? 'saved' : complete ? 'complete' : 'hidden';
  if (mode === 'hidden') return null;

  const label = mode === 'saving' ? 'Saving' : mode === 'saved' ? 'Saved' : 'Complete';

  return (
    <span
      className={`absolute right-4 top-4 z-10 flex h-7 items-center justify-center overflow-hidden rounded-full transition-[width,background-color,color] duration-300 ease-out motion-reduce:transition-none sm:right-5 sm:top-5 ${
        mode === 'saved'
          ? 'w-[5.25rem] gap-1 bg-emerald-600 px-2.5 text-white shadow-sm'
          : mode === 'saving'
            ? 'w-7 bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-200'
            : 'w-7 bg-emerald-100 text-emerald-700'
      }`}
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      {mode === 'saving' ? (
        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden />
      ) : mode === 'saved' ? (
        <>
          <Check size={14} strokeWidth={2.5} aria-hidden />
          <span className="text-xs font-semibold tracking-tight">Saved</span>
        </>
      ) : (
        <Check size={16} strokeWidth={2.5} aria-hidden />
      )}
    </span>
  );
}
